import * as React from "react"
import { motion } from "motion/react"

function chunkString(str: string): string[] {
  const words = str.split(" ")
  const chunks: string[] = []
  for (let i = 0; i < words.length; i += 2) {
    const chunk = words.slice(i, i + 2).join(" ") + " "
    chunks.push(chunk)
  }
  return chunks
}

export const AnimatedMarkup = ({
  children,
  isStreaming
}: {
  children: React.ReactNode
  isStreaming: boolean
}) => {
  const renderText = (node: React.ReactNode): React.ReactNode => {
    const noAnimate =
      React.isValidElement(node) &&
      "data-noanimate" in node.props &&
      (node.props as { "data-noanimate"?: boolean })["data-noanimate"] === true

    if (noAnimate) {
      return node
    }

    if (typeof node === "string") {
      const chunks = isStreaming ? chunkString(node) : [node]
      return chunks.flatMap((chunk, index) => {
        return (
          <motion.span
            key={`${index}-streaming`}
            animate={isStreaming ? { opacity: 1 } : {}}
            initial={isStreaming ? { opacity: 0 } : {}}
            transition={{ duration: 0.7 }}
          >
            {chunk}
          </motion.span>
        )
      })
    } else if (React.isValidElement(node)) {
      if (node.type === React.Fragment) {
        return React.cloneElement(
          node,
          { key: node.key },
          renderText((node.props as { children?: React.ReactNode }).children)
        )
      }
      return React.cloneElement(
        node,
        { ...(node.props ?? {}), "data-noanimate": true } as React.Attributes,
        renderText((node.props as { children?: React.ReactNode }).children)
      )
    } else if (Array.isArray(node)) {
      return node.map((child: React.ReactNode, index: number) => (
        <React.Fragment key={index}>{renderText(child)}</React.Fragment>
      ))
    }
    return null
  }

  if (!isStreaming) {
    return children
  }

  return renderText(children)
}
